import { useQuery } from "@tanstack/react-query";
import type { FilterTree, SubmissionStatus } from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  AdminSubmissionRow,
  AdminSubmissionsSortColumn,
} from "./types";

export type UseAdminSubmissionsOptions = {
  status?: SubmissionStatus | null;
  formId?: string | null;
  partnerId?: string | null;
  sortColumn?: AdminSubmissionsSortColumn;
  sortDir?: "asc" | "desc";
  /** Optional Saved View / AddFilterMenu FilterTree. */
  filterNode?: FilterTree | null;
  /** Gate the query (e.g. only fetch while a palette is open). Defaults to true. */
  enabled?: boolean;
};

// GET /api/admin/submissions?status=&form=&partner=&sort=&dir=&filter=
//
// Sub-bucket 17 — admin submissions queue list. Query key tuples every
// filter dimension onto the bare `queryKeys.submissions.list()` prefix so
// mutation hooks invalidate the bare key and TanStack prefix-matching
// sweeps every filter-cache automatically.
export function useAdminSubmissions(options: UseAdminSubmissionsOptions = {}) {
  const status = options.status ?? null;
  const formId = options.formId ?? null;
  const partnerId = options.partnerId ?? null;
  const sortColumn = options.sortColumn ?? "created_at";
  const sortDir = options.sortDir ?? "desc";
  const filterNode = options.filterNode ?? null;
  const filterHash = filterNode ? JSON.stringify(filterNode) : "none";

  return useQuery<AdminSubmissionRow[], ApiClientError>({
    queryKey: [
      ...queryKeys.submissions.list(),
      status,
      formId,
      partnerId,
      sortColumn,
      sortDir,
      filterHash,
    ],
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (formId) params.set("form", formId);
      if (partnerId) params.set("partner", partnerId);
      if (sortColumn) params.set("sort", sortColumn);
      if (sortDir) params.set("dir", sortDir);
      if (filterNode) params.set("filter", btoa(JSON.stringify(filterNode)));
      const qs = params.toString();
      const body = await apiFetch<{ submissions: AdminSubmissionRow[] }>(
        `/admin/submissions${qs ? `?${qs}` : ""}`,
      );
      return body.submissions;
    },
  });
}
